import { Observable, from, of } from 'rxjs';
import { catchError, switchMap, map, mergeMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { AxiosError } from 'axios';
import UploadService from '../services/upload.service';
import {
  UploadActions,
  UploadAction,
  UploadJobsRequest,
  UploadJobsSuccess,
  LoadUploads,
  LoadJobsError,
  LoadUploadsSuccess,
  UploadJobsError,
  LoadRows,
  LoadRowsSuccess,
  LoadRowsError,
  UpdateRow,
  UpdateRowSuccess, UpdateRowError, ProcessUpload, ProcessUploadSuccess, ProcessUploadError,
  ExportRecords, ExportRecordsSuccess, ExportRecordsError,
} from '../actions';
import { CreateErrorNotice, CreateSuccessNotice } from '../../store/actions/toaster.action';
import { BulkUploadTaskContainer, JobsUploadStatus } from '../../shared/models/jobs';

const uploadEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<UploadJobsRequest>(UploadActions.UploadRequest),
  switchMap((action) => from(UploadService.upload(action.file)).pipe(
    mergeMap((data: JobsUploadStatus) => of(
      new UploadJobsSuccess(data),
      new CreateSuccessNotice('Success', 'File was uploaded successfully.'),
    )),
    catchError((error: AxiosError) => of(
      // @ts-ignore
      error?.response?.data?.error && new UploadJobsError(error.response.data.error),
      new CreateErrorNotice('Error', 'Failed to upload jobs.'),
    )),
  )),
);

const loadUploadsEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<LoadUploads>(UploadActions.LoadUploads),
  // TODO: Check out observables timer
  switchMap(() => from(UploadService.getAllUploads()).pipe(
    map((data: JobsUploadStatus[]) => new LoadUploadsSuccess(data)),
    catchError((error: Error) => of(
      new LoadJobsError(error.message || 'Unknown upload fetching error.'),
      new CreateErrorNotice('Error', 'Failed to fetch uploads.'),
    )),
  )),
);

const loadRowsEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<LoadRows>(UploadActions.LoadRowsRequest),
  switchMap((action) => from(UploadService.getTasks(action.uploadID)).pipe(
    map((data: BulkUploadTaskContainer[]) => new LoadRowsSuccess(data)),
    catchError((error: Error) => of(
      new LoadRowsError(error.message || 'Unknown row fetching error.'),
      new CreateErrorNotice('Error', 'Failed to fetch rows.'),
    )),
  )),
);

const updateRowEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<UpdateRow>(UploadActions.UpdateRow),
  switchMap((action) => from(UploadService.updateTask(action.uploadID, action.taskID, action.payload)).pipe(
    map((task: BulkUploadTaskContainer) => new UpdateRowSuccess(task)),
    catchError((error: Error) => of(
      new UpdateRowError(error.message || 'Unknown row updating error.'),
      new CreateErrorNotice('Error', 'Failed to update row.'),
    )),
  )),
);

const processUploadEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<ProcessUpload>(UploadActions.ProcessUpload),
  switchMap((action) => from(UploadService.processUpload(action.uploadID)).pipe(
    mergeMap((status: JobsUploadStatus) => of(
      new ProcessUploadSuccess(status),
      new CreateSuccessNotice('Success', 'Upload has been processed!'),
    )),
    catchError((error: Error) => of(
      new ProcessUploadError(error.message || 'Unknown upload processing error.'),
      new CreateErrorNotice('Error', 'Failed to process upload'),
    )),
  )),
);

const exportRecordsEpic = (action$: Observable<UploadAction>) => action$.pipe(
  ofType<ExportRecords>(UploadActions.ExportRecords),
  switchMap((action) => from(UploadService.export(action.ids)).pipe(
    mergeMap(() => of(
      new ExportRecordsSuccess(),
      new CreateSuccessNotice('Success', 'Records have been exported!'),
    )),
    catchError((error: Error) => of(
      new ExportRecordsError(error.message || 'Unknown records exporting error.'),
      new CreateErrorNotice('Error', 'Failed to export records'),
    )),
  )),
);

export default [
  uploadEpic,
  loadUploadsEpic,
  loadRowsEpic,
  updateRowEpic,
  processUploadEpic,
  exportRecordsEpic,
];